/**
 * Upgrades: a machine's levels as a track (what each holds, takes in and reaches, and what reaching
 * it costs and takes), where the machine stands on it, its upgrade (on offer or under way), and
 * starting one. All of it comes from the rules' level rows (`MACHINES[type].levels`): an upgrade is
 * a row there, and a look in world/models/machines.ts when it should look different. Nothing here
 * names a level. Only extractors have levels: a charge goes off once.
 */
import {
  MACHINES,
  machineAt,
  nextLevelOf,
  type Cost,
  type MachineLevel,
  type MachineSpec,
  type MachineType,
} from '@bramvasterd/terrella-rules';

import type { Game } from '../game';
import { actionCheck, submitAction, type ActionCheck } from '../intents/submit';
import { predictTime } from '../session/predict';

/** The machines whose next level is in the works: their track ends with it, coming soon. */
export const LEVELS_COMING: ReadonlySet<MachineType> = new Set<MachineType>(['crystal', 'stone']);

/** What a level can raise, by the machine's role, in the order the sheet shows them. */
export type UpgradeStat = 'storage' | 'ingest' | 'range';
export const STATS_OF: Readonly<Record<MachineSpec['role'], readonly UpgradeStat[]>> = {
  extractor: ['storage', 'ingest', 'range'],
  charge: [],
  infuser: [],
};
export type StatValues = Readonly<Partial<Record<UpgradeStat, number>>>;

/** Where a machine stands to one of its levels. */
export type LevelState = 'past' | 'current' | 'next' | 'later';

/** One level on a machine's track. */
export interface TrackLevel {
  level: number;
  state: LevelState;
  values: StatValues;
  /** How much each value is up on the level before (all 0 for level 1). */
  gains: StatValues;
  /** What upgrading to it costs and how long it takes; level 1 is placed, so nothing. */
  cost: Cost;
  ms: number;
}

export type Upgrade =
  /** It can become level `to`: what that costs and takes, and whether I can start it now. */
  | { kind: 'offer'; to: number; cost: Cost; ms: number; check: ActionCheck }
  /** It becomes level `to` at server time `done`: `left` from now, `progress` from 0 to 1. */
  | { kind: 'underway'; to: number; done: number; left: number; progress: number };

/** A machine's levels in order, its upgrade, and the level announced after its last. */
export interface UpgradeTrack {
  /** The values its role shows, in order. */
  stats: readonly UpgradeStat[];
  levels: readonly TrackLevel[];
  /** On offer, under way, or null at its last level. */
  upgrade: Upgrade | null;
  /** The level after its last, coming soon; null when none is announced. */
  comingSoon: number | null;
}

/** A level row's values, by the stats its role has. */
function valuesOf(stats: readonly UpgradeStat[], row: MachineLevel): StatValues {
  const out: Partial<Record<UpgradeStat, number>> = {};
  for (const stat of stats) out[stat] = (row as Partial<Record<UpgradeStat, number>>)[stat] ?? 0;
  return out;
}

/** The upgrade of the machine on `tile`; null when none stands there, or it is at its last level. */
export function upgradeOf(game: Game, tile: number): Upgrade | null {
  const view = game.store.get().view;
  const machine = view ? machineAt(view, tile) : null;
  const spec = machine ? MACHINES[machine.type] : null;
  if (!view || !machine || spec?.role !== 'extractor') return null;
  const levels: readonly MachineLevel[] = spec.levels;
  if (machine.upgrade) {
    const { to, done } = machine.upgrade;
    const ms = levels[to - 1]?.upgradeMs ?? 0;
    const left = Math.max(0, done - predictTime(game));
    return { kind: 'underway', to, done, left, progress: ms > 0 ? 1 - Math.min(1, left / ms) : 1 };
  }
  const next = nextLevelOf(machine.type, machine.level);
  if (!next) return null;
  return {
    kind: 'offer',
    to: machine.level + 1,
    cost: next.cost,
    ms: next.upgradeMs,
    check: actionCheck(game, { kind: 'upgrade', tile }),
  };
}

/** The track of the machine on `tile`; null when none stands there. */
export function upgradeTrack(game: Game, tile: number): UpgradeTrack | null {
  const view = game.store.get().view;
  const machine = view ? machineAt(view, tile) : null;
  const spec = machine ? MACHINES[machine.type] : null;
  if (!machine || spec?.role !== 'extractor') return null;
  const stats = STATS_OF[spec.role];
  const levels: readonly MachineLevel[] = spec.levels;
  const stateOf = (level: number): LevelState =>
    level < machine.level
      ? 'past'
      : level === machine.level
        ? 'current'
        : level === machine.level + 1
          ? 'next'
          : 'later';
  return {
    stats,
    levels: levels.map((row, k) => {
      const before = levels[k - 1];
      const values = valuesOf(stats, row);
      const was = before ? valuesOf(stats, before) : null;
      const gains: Partial<Record<UpgradeStat, number>> = {};
      for (const stat of stats) gains[stat] = was ? (values[stat] ?? 0) - (was[stat] ?? 0) : 0;
      return {
        level: k + 1,
        state: stateOf(k + 1),
        values,
        gains,
        cost: before ? row.cost : {},
        ms: before ? row.upgradeMs : 0,
      };
    }),
    upgrade: upgradeOf(game, tile),
    comingSoon: LEVELS_COMING.has(machine.type) ? levels.length + 1 : null,
  };
}

/** Start upgrading the machine on `tile`; false, with the rules' refusal, when it can't now. */
export function startUpgrade(game: Game, tile: number): boolean {
  return submitAction(game, { kind: 'upgrade', tile });
}
